import {
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";

/**
 * Thin wrapper over MUI Table for the list pages.
 *
 * `columns` is `[{ key, label, align, render(row) }]`; `pagination` is the
 * envelope the API returns ({ page, limit, total }). Pass `onPageChange` /
 * `onLimitChange` to make the footer live.
 */
export default function DataTable({
    columns = [],
    rows = [],
    loading = false,
    emptyMessage = "Nothing here yet",
    pagination,
    onPageChange,
    onLimitChange,
    onRowClick,
}) {
    return (
        <TableContainer sx={{ bgcolor: "var(--bg-secondary)", borderRadius: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || "left"}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={columns.length}>
                                <Stack sx={{ alignItems: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )}

                    {!loading && rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length}>
                                <Stack sx={{ alignItems: "center", py: 4 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "var(--text-secondary)" }}
                                    >
                                        {emptyMessage}
                                    </Typography>
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )}

                    {!loading &&
                        rows.map((row, i) => (
                            <TableRow
                                key={row.id ?? i}
                                hover={!!onRowClick}
                                onClick={() => onRowClick?.(row)}
                                sx={{ cursor: onRowClick ? "pointer" : "default" }}
                            >
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align={col.align || "left"}
                                    >
                                        {col.render
                                            ? col.render(row)
                                            : (row[col.key] ?? "—")}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                </TableBody>
            </Table>

            {pagination && (
                <TablePagination
                    component="div"
                    count={pagination.total ?? 0}
                    page={Math.max(0, (pagination.page ?? 1) - 1)}
                    rowsPerPage={pagination.limit ?? 25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onPageChange={(_e, p) => onPageChange?.(p + 1)}
                    onRowsPerPageChange={(e) =>
                        onLimitChange?.(parseInt(e.target.value, 10))
                    }
                />
            )}
        </TableContainer>
    );
}
